<template>
    <div class="sidebar-thin rra-autogen custom-container">
        <TheSidebar/>
        <ThePageHeadingBar
            :pageHeading="`${ $route.meta.title } RRAs`"
        />
        <section class="main">
            <v-form
                ref="form"
                noValidate
            >
                <v-row>
                <!-- First Column -->
                <v-col cols="4">
                    <!-- Location -->
                    <div>
                        <label for="location">
                            Location
                            <RequiredAsterisk/>
                        </label>
                        <DropdownLocationList
                            id="location"
                            v-model="models.location"
                            outlined
                            :rules="[ rules.requiredObject ]"
                            validateOnBlur
                        />
                    </div>
                    <!-- Supplier -->
                    <div class="rra-autogen__option supplier">
                        <label
                            for="supplier-table"
                            class="supplier__label"
                        >
                            <span>
                                Suppliers
                                <RequiredAsterisk/>
                            </span>
                            <v-switch
                                v-model="models.allSuppliersSelected"
                                class="supplier__switch"
                                label="All"
                                hideDetails
                            />
                        </label>
                        <div
                            v-if="models.allSuppliersSelected"
                            class="supplier__switch--all-selected-text"
                        >
                            <p>This will generate RRAs based on all suppliers.</p>
                            <p>To filter specific suppliers, switch 'All' off.</p>
                        </div>
                        <TableBaseFilterable
                            id="supplier-table"
                            v-else
                            class="supplier__table"
                            :class="supplierTableClasses"
                            :headers="supplierTable.headers"
                            :filter.sync="supplierTable.filter"
                            :items="supplierList.map( ( supplier ) => ( { id: supplier.id, name: supplier.name } ) )"
                            :loading="!hasSupplierList"
                            :selected.sync="models.suppliers"
                            :noDataText="( !hasSupplierList ? 'Searching for Suppliers...' : 'No Suppliers Available' )"
                            cardElevation="3"
                            filterLabel="Filter Suppliers..."
                            :itemsPerPage="-1"
                            showSelect
                            hideAdd
                        />
                    </div>
                </v-col>
                <!-- Second Column -->
                <v-col cols="4">
                    <!-- Sourcing End Date -->
                    <div>
                        <label for="sourcing-end-date">
                            Sourcing End Date
                            <RequiredAsterisk/>
                        </label>
                        <!-- NOTE: Leaving the isDateGreaterThanToday rule but I am not sure why we still need it. -->
                        <!-- By setting a min and max selectable date, we should not need to check if the date is greater than today. -->
                        <InputDatePicker
                            label="Sourcing End Date"
                            :date.sync="models.sourcingEndDate.value"
                            :min-date="models.sourcingEndDate.min"
                            :max-date="models.sourcingEndDate.max"
                            outlined
                            :additional-rules="[rules.required, isDateGreaterThanToday]"
                        />
                    </div>
                    <v-row
                        noGutters
                        class="rra-autogen__option flex-nowrap justify-space-between"
                    >
                        <!-- Minimum Unit Count -->
                        <div class="minimum-unit-count">
                            <label for="minimum-unit-count">
                                Minimum Unit Count
                            </label>
                            <v-text-field
                                v-model="models.minimumUnitCount"
                                type="number"
                                dense
                                hideDetails
                                outlined
                            />
                        </div>
                        <!-- Condition -->
                        <div>
                            <label for="condition">
                                Condition
                            </label>
                            <DropdownRRACondition v-model="models.condition"/>
                        </div>
                    </v-row>
                    <!-- Supplier Parameters -->
                    <div class="rra-autogen__option">
                        <label for="supplier-parameters">
                            Supplier Parameters
                        </label>
                        <v-checkbox
                            v-model="models.anySupplierParameter"
                            label="Any"
                            hideDetails
                        />
                        <v-checkbox
                            v-model="models.authRequired"
                            label="Authorization Required"
                            hideDetails
                        />
                        <v-checkbox
                            v-model="models.possibleRestockingFee"
                            label="Possible Restocking Fee"
                            hideDetails
                        />
                    </div>
                    <!-- Supplier Return Days -->
                    <div class="rra-autogen__option">
                        <label for="supplier-return-days">
                            Supplier Return Days
                        </label>
                        <v-range-slider
                            id="supplier-return-days"
                            v-model="models.returnDays.range"
                            :min="models.returnDays.min"
                            :max="models.returnDays.max"
                            class="supplier-return-days"
                            hideDetails
                        >
                            <template v-slot:prepend>
                                <v-text-field
                                    :value="models.returnDays.range[ 0 ]"
                                    class="supplier-return-days__input"
                                    type="number"
                                    dense
                                    hidDetails
                                    outlined
                                    singleLine
                                    @change="$set( models.returnDays.range, 0, $event )"
                                />
                            </template>
                            <template v-slot:append>
                                <v-text-field
                                    :value="models.returnDays.range[ 1 ]"
                                    class="supplier-return-days__input"
                                    type="number"
                                    dense
                                    hideDetails
                                    outlined
                                    singleLine
                                    @change="$set( models.returnDays.range, 1, $event )"
                                />
                            </template>
                        </v-range-slider>
                    </div>
                    <!-- Email Recipients -->
                    <div>
                        <label for="email-recipients">
                            Email Recipients
                            <RequiredAsterisk/>
                        </label>
                        <v-text-field
                            id="email-recipients"
                            v-model="models.emailRecipients"
                            :rules="[ rules.required, rules.isSemicolonDelimitedEmailList ]"
                            dense
                            outlined
                            hideDetails
                        />
                    </div>
                    <!-- RRA Notes -->
                    <div class="rra-autogen__option">
                        <label for="rra-notes">
                            RRA Notes
                        </label>
                        <v-textarea
                            id="rra-notes"
                            v-model="models.rraNotes"
                            placeholder="RRA Notes"
                            dense
                            outlined
                            hideDetails
                        />
                    </div>
                </v-col>
                <!-- Third Column -->
                <v-col cols="4">
                    <div>
                        <v-row noGutters>
                            <label for="title-designators">
                                Title Designators
                            </label>
                            <v-spacer/>
                            <ClButton
                                class="button-reset-designators"
                                type="tertiary"
                                plain
                                @click="resetTitleDesignators()"
                            >
                                Reset Designators
                            </ClButton>
                        </v-row>
                        <SelectIncludeExclude
                            id="title-designators"
                            ref="title-designators"
                            v-if="hasTitleDesignatorList"
                            :initialOptions="titleDesignators"
                            :showResetButton="false"
                            @updateOptions="( include, exclude ) => updateTitleDesignators( include, exclude )"
                        />
                        <v-progress-linear
                            v-else
                            class="loader"
                            color="primary"
                            size="100"
                            width="5"
                            indeterminate
                        />
                    </div>
                </v-col>
            </v-row>
            </v-form>
        </section>
        <ThePageFooterBar>
            <v-row>
                <v-spacer/>
                <v-btn
                    :disabled="inFlight"
                    :loading="inFlight"
                    class="button-preview"
                    color="primary"
                    @click="previewButtonHandler()"
                >
                    Preview
                </v-btn>
                <v-btn
                    :disabled="inFlight"
                    :loading="inFlight"
                    class="button-autogen"
                    color="primary"
                    @click="autogenButtonHandler()"
                >
                    Auto Generate
                </v-btn>
            </v-row>
        </ThePageFooterBar>
    </div>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Utils
import DateUtil from '@/utils/DateUtil';
import FormatUtil from '@/utils/FormatUtil';
import ValidationUtil from '@/utils/ValidationUtil';

// Components
import TheSidebar from '@/views/common/TheSidebar';
import ThePageHeadingBar from '@/components/ThePageHeadingBar';
import ThePageFooterBar from '@/components/ThePageFooterBar';

import DropdownLocationList from '@/views/common/dropdowns/DropdownLocationList';
import RequiredAsterisk from '@/components/base/RequiredAsterisk';
import SelectIncludeExclude from '@/components/base/multioption/SelectIncludeExclude';
import TableBaseFilterable from '@/components/base/table/TableBaseFilterable';

import InputDatePicker from '@/components/InputDatePicker';
import DropdownRRACondition from './autoGen/DropdownRRACondition';

export default {
    name: 'RRAAutoGen',
    components: {
        InputDatePicker,
        TheSidebar,
        ThePageHeadingBar,
        ThePageFooterBar,
        DropdownLocationList,
        RequiredAsterisk,
        SelectIncludeExclude,
        TableBaseFilterable,
        DropdownRRACondition,
    },
    data() {
        return {
            rules: {
                required: ValidationUtil.required,
                requiredObject: ValidationUtil.requiredObject,
                isSemicolonDelimitedEmailList: ValidationUtil.isSemicolonDelimitedEmailList,
            },
            supplierTable: {
                isMissingRequiredSelection: false,
                filter: '',
                headers: [
                    {
                        text: 'Supplier',
                        value: 'name',
                    },
                ],
            },
            // model bindings with defaults
            models: {
                /** @type { import('@/typedefs/location').location } */
                location: {},

                /** @type { import('@/typedefs/primarySupplier').primarySupplier[] } */
                suppliers: [],
                allSuppliersSelected: true,

                sourcingEndDate: {
                    /** @type { String } - 'YYYY-MM-DD' - initialize to 90 days out from today's date */
                    value: FormatUtil.epochToDate( DateUtil.addDays( new Date(), 90 ).getTime() ),
                    /** @type { String } - 'YYYY-MM-DD' */
                    min: new Date()
                        .toISOString()
                        .substring( 0, 10 ),
                    /** @type { String } - 'YYYY-MM-DD' */
                    max: new Date( new Date().setFullYear( new Date().getFullYear() + 5 ) )
                        .toISOString()
                        .substring( 0, 10 ),
                },

                /** @type { Number } */
                minimumUnitCount: 10,

                /** @type { String } */
                condition: 'All',

                // Supplier Parameters
                anySupplierParameter: true,
                authRequired: false,
                possibleRestockingFee: false,

                returnDays: {
                    // range[0] is start days, range[1] is end days
                    range: [ 1, 999 ],
                    min: 1,
                    max: 999,
                },

                /** @type { String } - a semicolon delimited list of emails */
                emailRecipients: '',

                /** @type { String } */
                rraNotes: 'This document was auto-generated.',

                titleDesignators: {
                    /** @type { import('@/typedefs/titleDesignator').titleDesignator[] } */
                    included: [],
                    /** @type { import('@/typedefs/titleDesignator').titleDesignator[] } */
                    excluded: [],
                },
            },
        };
    },
    computed: {
        ...mapGetters( {
            hasLocationList: 'GlobalResources/hasLocationList',
            hasSupplierList: 'RRA/AutoGen/hasSupplierList',
            hasTitleDesignatorList: 'GlobalResources/hasTitleDesignatorList',
        } ),
        ...mapState( {
            inFlight: ( state ) => state.RRA.AutoGen.inFlight,
            supplierList: ( state ) => state.RRA.AutoGen.suppliers.list,
            titleDesignators: ( state ) => state.GlobalResources.titleDesignatorList,
            username: ( state ) => state.User.username,
        } ),
        /** @returns { Boolean } */
        hasSelectedLocation() {
            return 'id' in this.models.location;
        },
        /** @returns { String } */
        supplierTableClasses() {
            return this.supplierTable.isMissingRequiredSelection
                ? 'error-border'
                : '';
        },
        todayAsString() {
            return new Date().toISOString().substring( 0, 10 );
        },
    },
    watch: {
        // eslint-disable-next-line object-shorthand
        'models.anySupplierParameter'( value ) {
            if ( value ) {
                this.models.authRequired = false;
                this.models.possibleRestockingFee = false;
            }
        },
        // eslint-disable-next-line object-shorthand
        'models.authRequired'( value ) {
            if ( value ) {
                this.models.anySupplierParameter = false;
            }
        },
        // eslint-disable-next-line object-shorthand
        'models.possibleRestockingFee'( value ) {
            if ( value ) {
                this.models.anySupplierParameter = false;
            }
        },
    },
    mounted() {
        // Grab locations, and title designators
        this.$store.dispatch( 'GlobalResources/init' );
        // Grab a filtered list of suppliers that allow returns
        this.$store.dispatch( 'RRA/AutoGen/init' );
        // Begin a semicolon delimited list of email addresses
        this.models.emailRecipients = `${ this.username };`;
    },
    methods: {
        checkSupplierTableRequiredSelection() {
            // Toggle isMissingRequiredSelection to true if allSuppliersSelected is false and we don't have any suppliers selected
            this.supplierTable.isMissingRequiredSelection = !this.models.allSuppliersSelected && this.models.suppliers.length === 0;
        },
        clearValidate() {
            this.supplierTable.isMissingRequiredSelection = false;
            return this.$refs.form.resetValidation();
        },
        validate() {
            this.checkSupplierTableRequiredSelection();
            return this.$refs.form.validate()
                && !this.supplierTable.isMissingRequiredSelection;
        },
        updateTitleDesignators( include, exclude ) {
            this.models.titleDesignators.included = include;
            this.models.titleDesignators.excluded = exclude;
        },
        resetTitleDesignators() {
            this.$refs[ 'title-designators' ].reset();
        },
        getAutoGenPayload() {
            return {
                locationDTO: this.models.location,
                supplierIds: this.models.allSuppliersSelected
                    ? this.supplierList.map( ( supplier ) => supplier.id )
                    : this.models.suppliers.map( ( supplier ) => supplier.id ),
                sourcingEndDate: this.models.sourcingEndDate.value,
                minimumUnitCount: this.models.minimumUnitCount,
                includeNewCondition: [ 'All', 'New' ].includes( this.models.condition ),
                includeUsedCondition: [ 'All', 'Used' ].includes( this.models.condition ),
                anySupplierParameter: this.models.anySupplierParameter,
                authorizationRequired: this.models.authRequired,
                restockingFee: this.models.possibleRestockingFee,
                returnDaysStart: this.models.returnDays.range[ 0 ],
                returnDaysEnd: this.models.returnDays.range[ 1 ],
                emailRecipients: this.models.emailRecipients,
                rraNotes: this.models.rraNotes,
                titleIncludeDesignatorList: this.models.titleDesignators.included,
                titleExcludeDesignatorList: this.models.titleDesignators.excluded,
            };
        },
        async previewButtonHandler() {
            if ( this.validate() ) {
                await this.$store.dispatch( 'RRA/AutoGen/preview', this.getAutoGenPayload() );
                this.clearValidate();
            }
        },
        async autogenButtonHandler() {
            if ( this.validate() ) {
                await this.$store.dispatch( 'RRA/AutoGen/generate', this.getAutoGenPayload() );
                this.clearValidate();
            }
        },
        isDateGreaterThanToday( val ) {
            return new Date( val ) >= new Date( this.todayAsString );
        },
    },
};
</script>

<style scoped lang="scss">
.rra-autogen {
    &__column {
        display: flex;
        flex-flow: column nowrap;
        justify-content: space-between;
    }
    &__option {
        margin-top: 2rem;
    }
    .minimum-unit-count {
        margin-right: 1rem;
    }
    label {
        margin-bottom: 1rem;
        font-weight: bold;
    }

    .supplier {
        &__label {
            display: flex;
            flex-flow: row nowrap;
            justify-content: space-between;
        }
        &__switch {
            margin-top: 0;
            &--all-selected-text {
                border: 1px solid #9e9e9e;
                border-radius: 4px;
                padding: 1rem;
                min-height: 65vh;
            }
        }
        &__table {
            margin-top: .5rem;
            max-height: 65vh;
            overflow-y: scroll;
            &.error-border {
                color: var(--v-error-base);
                border: 2px solid currentColor;
            }
        }
    }

    .supplier-return-days {
        &__input {
            margin-top: 0;
            padding-top: 0;
            width: 80px;
        }
    }

    .button-reset-designators .v-btn {
        text-transform: unset;
        margin-top: -0.4rem;
    }
    ::v-deep .select-include-exclude ul {
        margin: 0;
    }

    .button-autogen {
        margin-left: 1rem;
    }
}
</style>
